import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip, Link as MuiLink } from '@mui/material';
import Box from '@mui/material/Box';

import { palette, spaces } from '../../../../../assets/styles/themes/tokens';
import { routes } from '../../../../../constants';
import { compressClassYearArray } from '../../../../../utils/compressClassYearArray';
import useRouter from '../../../../../utils/useRouter';
import Tooltip from '../../../../Tooltip';
import { AddMenuButton, mapContentTypeName, Metadata, MuiCardBaseProps } from '../MuiCardBase';
import CardWrapper from './MuiCardBaseLongWrapper';
import CardSection from './MuiCardBaseLongSection';
import CardContent from './MuiCardBaseLongContent';
import CardHeader from './MuiCardBaseLongHeader';
import CardTextsWrapper from './MuiCardBaseLongTextsWrapper';
import CardTitle from './MuiCardBaseLongTitle';
import CardDescription from '../../../common/CardDescription/NewCardDescription';
import CardMetadataWrapper from './MuiCardBaseLongMetadataWrapper';
import CardCover from '../MuiCardBaseCover';

export const MuiCardBaseLong: React.FC<MuiCardBaseProps> = (props) => {
  const {
    id,
    contentType,
    publisher,
    index,
    isReadOnly,
    cover,
    title,
    description,
    subjects,
    classYears,
    link,
    linkFunction,
    date,
    chipText,
    isPinned,
    isNew,
    itemData,
  } = props;

  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const itemLink: string =
    contentType === 'product'
      ? `/${routes.productPage.root}/${id}`
      : `/${routes.reader.root}/${id}`;

  return (
    <CardWrapper
      contentType={contentType}
      itemLink={itemLink}
      isNew={isNew}
      isExpanded={isExpanded}
    >
      <AddMenuButton
        isPinned={isPinned}
        type={contentType}
        id={id}
        itemData={itemData}
        index={index}
      />
      <CardCover
        itemLink={itemLink}
        isWide={contentType === 'video'}
        withPlayIcon={contentType === 'video'}
        cover={cover}
      >
        <Box
          data-testid='mui-card-desktop__pages-chip'
          sx={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            bottom: spaces.md,
          }}
        >
          {contentType !== 'video' && (
            <Chip label={chipText} color='secondary' size='small' />
          )}
        </Box>
      </CardCover>
      <CardSection>
        <CardContent>
          <CardHeader
            primaryText={t(mapContentTypeName(contentType))}
            secondaryText={publisher}
            showReadOnlyChip={isReadOnly}
            customReadOnlySx={{ marginY: '-5.5px' }}
          />
          <CardTextsWrapper>
            <CardTitle title={title} itemLink={itemLink} />
            <CardDescription 
              description={description}
              isExpanded={isExpanded} 
              onShowToggleClick={() => setIsExpanded(!isExpanded)} />
          </CardTextsWrapper>
        </CardContent>
        <CardMetadataWrapper>
          {subjects && subjects.length > 0 && (
            <SubjectsMetadata subjects={subjects} />
          )}
          {classYears && classYears.length > 0 && (
            <ClassYearsMetadata classYears={classYears} />
          )}
          {date && <Metadata icon='calendar' label={date} />}
          {link && <LinkSection link={link} linkFunction={linkFunction} />}
        </CardMetadataWrapper>
      </CardSection>
    </CardWrapper>
  );
};

const LinkSection = ({
  link,
  linkFunction,
}: {
  link: NonNullable<MuiCardBaseProps['link']>;
  linkFunction?: () => void;
}) => {
  const { router } = useRouter();

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    to: string
  ) => {
    e.stopPropagation();
    linkFunction ? linkFunction() : router.push(to);
  };

  return (
    <Tooltip
      label={link.label}
      open={link.hasTooltip ? undefined : false}
      placement='top-start'
    >
      <MuiLink
        underline='none'
        onClick={(e) => handleLinkClick(e, link.to)}
        data-testid='mui-card-link'
      >
        <Metadata
          icon={link.icon}
          label={link.label}
          sx={{ color: undefined, stroke: undefined }}
        />
      </MuiLink>
    </Tooltip>
  );
};

const SubjectsMetadata = ({
  subjects,
}: {
  subjects: NonNullable<MuiCardBaseProps['subjects']>;
}) => (
  <Tooltip label={subjects.join(', ')} placement='top-start'>
    <Metadata
      icon='tag'
      sx={{ color: palette.text.secondary, maxWidth: 218 }}
      label={subjects.join(', ')}
    />
  </Tooltip>
);

const ClassYearsMetadata = ({
  classYears,
}: {
  classYears: NonNullable<MuiCardBaseProps['classYears']>;
}) => {
  const { t } = useTranslation();

  return (
    <Metadata
      icon='teacher'
      sx={{ color: palette.text.secondary }}
      label={`${compressClassYearArray(classYears).join(', ')} ${t('Class year')}`}
    />
  );
};

export default MuiCardBaseLong;
